import { Card, Col, Row, Statistic, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  EyeOutlined,
  InboxOutlined,
  MessageOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import type { ReactNode } from 'react';
import { totalViews, type MetricCounts } from '@entities/analytics';

const { Text } = Typography;

interface CardDef {
  key: 'views' | 'requests' | 'chats' | 'deals';
  icon: ReactNode;
  gradient: string;
  iconColor: string;
  value: (t: MetricCounts) => number;
}

const CARDS: CardDef[] = [
  {
    key: 'views',
    icon: <EyeOutlined />,
    gradient: 'linear-gradient(135deg, rgba(37,99,235,0.18), rgba(37,99,235,0))',
    iconColor: '#2563eb',
    value: totalViews,
  },
  {
    key: 'requests',
    icon: <InboxOutlined />,
    gradient: 'linear-gradient(135deg, rgba(245,158,11,0.18), rgba(245,158,11,0))',
    iconColor: '#d97706',
    value: (t) => t.REQUEST_RECEIVED,
  },
  {
    key: 'chats',
    icon: <MessageOutlined />,
    gradient: 'linear-gradient(135deg, rgba(16,185,129,0.18), rgba(16,185,129,0))',
    iconColor: '#10b981',
    value: (t) => t.CHAT_STARTED,
  },
  {
    key: 'deals',
    icon: <TrophyOutlined />,
    gradient: 'linear-gradient(135deg, rgba(139,92,246,0.18), rgba(139,92,246,0))',
    iconColor: '#8b5cf6',
    value: (t) => t.DEAL_WON,
  },
];

export function KpiCards({ totals }: { totals: MetricCounts }) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  return (
    <Row gutter={[16, 16]}>
      {CARDS.map(({ key, icon, gradient, iconColor, value }) => (
        <Col xs={24} sm={12} lg={6} key={key}>
          <Card
            bordered
            style={{
              borderRadius: 12,
              background: gradient,
              borderColor: token.colorBorderSecondary,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Statistic
                title={<Text style={{ fontSize: 13, fontWeight: 500 }}>{t(`dashboard.kpi.${key}`)}</Text>}
                value={value(totals)}
                valueStyle={{ fontSize: 28, fontWeight: 700 }}
              />
              <div
                aria-hidden
                style={{
                  width: 40,
                  height: 40,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 10,
                  background: 'var(--ant-color-bg-container)',
                  color: iconColor,
                  fontSize: 20,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                }}
              >
                {icon}
              </div>
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t(`dashboard.kpi.${key}Hint`)}
            </Text>
          </Card>
        </Col>
      ))}
    </Row>
  );
}
